import * as React from 'react';
import { isRouteGroup, Route, RouteItem } from '../types';
import { MachinesHomePage } from './pages/maintenance';
import {
    ProductionPerformance,
    EmployeesHomePage,
    OrderAdjustmentsHomePage,
    OrderProductionsHomePage,
    ProductInventory,
    ProductionPlanning,
    ProductsHomePage,
    ProductsToProduce,
} from './pages/production';
import {
    ClientsHomePage,
    OrderQuotationsHomePage,
    OrderRequestsHomePage,
    OrderSalesHomePage,
    OrdersToFulfill,
    ReturnsHomePage,
} from './pages/sales';
import { ProductionSummary } from './pages/production/production-summary/production-summary';
import { MaterialBalance } from './pages/production/material-balance/material-balance';
import { ActivitiesPage } from './pages/activities/activities';
import { UsersHomePage } from './pages/auth/users/users-home-page';
import { RoleId } from '../services';
import { SalesSummary } from './pages/sales/sales-summary/sales-summary';
import {
    AccountsHomePage,
    ExpensesHomePage,
    TransfersHomePage,
} from './pages/management';
import { ResourcesHomePage } from './pages/management/resources/resources-home-page';
import { BalancesPage } from './pages/management/balances/balances-page';
import { Accounting } from './pages/management/accounting/accounting';
import { ExpensesSummary } from './pages/management/expenses-summary/expenses-summary';
import { SalesVsExpensesHistoric } from './pages/management/admin-dashboard/admin-dashboard';
import { ThirdPartyBalances } from './pages/management/third-party-balances/third-party-balances';
import { DashboardPage } from './pages/dashboard';
import { PayrollHomePage } from './pages/payroll/payroll-home-page';

// Domain role sets. The cross-domain hierarchy (Super sees all; General/Admin
// and Asistente General/Guest see every non-super route) lives in
// routeAuthFilter, so these lists only carry the domain-specific roles.
const PRODUCTION_DOMAIN = [RoleId.Production, RoleId.ProductionAssistant];
const SALES_DOMAIN = [RoleId.Sales, RoleId.SalesAssistant];
const EXPENSES_DOMAIN = [RoleId.Expenses, RoleId.ExpensesAssistant];
const HR_DOMAIN = [RoleId.HumanResources, RoleId.HumanResourcesAssistant];
// Cross-domain admin view (Activities): Super + General + Asistente General
// only — domain roles cannot see it.
const GENERAL_VIEW = [RoleId.Admin, RoleId.Guest];

export const routeItems: RouteItem[] = [
    {
        name: 'DashboardPage',
        path: '/dashboard',
        component: <DashboardPage />,
        title: 'Dashboard',
        navbar: false,
        // No roleIds → visible to every authenticated user.
    },
    {
        title: 'Producción',
        navbar: true,
        roleIds: PRODUCTION_DOMAIN,
        routes: [
            {
                name: 'MachinesHome',
                path: '/machines',
                isNested: true,
                component: <MachinesHomePage />,
                title: 'Máquinas',
                navbar: true,
                roleIds: PRODUCTION_DOMAIN,
            },
            {
                name: 'ProductsHome',
                path: '/products',
                isNested: true,
                component: <ProductsHomePage />,
                title: 'Productos',
                navbar: true,
                roleIds: PRODUCTION_DOMAIN,
            },
            {
                name: 'OrderProductionProductsHomePage',
                path: '/orderProductionProducts',
                isNested: true,
                component: <OrderProductionsHomePage />,
                title: 'Producción',
                navbar: true,
                roleIds: PRODUCTION_DOMAIN,
            },
            {
                name: 'OrderAdjustmentsHomePage',
                path: '/orderAdjustments',
                isNested: true,
                component: <OrderAdjustmentsHomePage />,
                title: 'Ajustes',
                navbar: true,
                roleIds: PRODUCTION_DOMAIN,
            },
            {
                name: 'ProductInventoryPage',
                path: '/productInventory',
                isNested: false,
                component: <ProductInventory />,
                title: 'Inventario',
                navbar: true,
                roleIds: PRODUCTION_DOMAIN,
            },
            {
                name: 'ProductsToProducePage',
                path: '/productsToProduce',
                isNested: false,
                component: <ProductsToProduce />,
                title: 'Productos por producir',
                navbar: true,
                roleIds: PRODUCTION_DOMAIN,
            },
            {
                name: 'ProductionPlanningPage',
                path: '/productionPlanning',
                isNested: false,
                component: <ProductionPlanning />,
                title: 'Planeación',
                navbar: true,
                roleIds: PRODUCTION_DOMAIN,
            },
            {
                name: 'ProductionSummaryPage',
                path: '/productionSummary',
                isNested: false,
                component: <ProductionSummary />,
                title: 'Resumen de producción',
                navbar: true,
                roleIds: PRODUCTION_DOMAIN,
            },
            {
                name: 'ProductionPerformancePage',
                path: '/productionPerformance',
                isNested: false,
                component: <ProductionPerformance />,
                title: 'Rendimiento de producción',
                navbar: true,
                roleIds: PRODUCTION_DOMAIN,
            },
            {
                name: 'MaterialBalancePage',
                path: '/materialBalance',
                isNested: false,
                component: <MaterialBalance />,
                title: 'Balance de materia',
                navbar: true,
                roleIds: PRODUCTION_DOMAIN,
            },
        ],
    },
    {
        title: 'Ventas',
        navbar: true,
        roleIds: SALES_DOMAIN,
        routes: [
            {
                name: 'OrderQuotationsHome',
                path: '/orderQuotations',
                isNested: true,
                component: <OrderQuotationsHomePage />,
                title: 'Cotizaciones',
                navbar: true,
                roleIds: SALES_DOMAIN,
            },
            {
                name: 'OrderRequestsHome',
                path: '/orderRequests',
                isNested: true,
                component: <OrderRequestsHomePage />,
                title: 'Pedidos',
                navbar: true,
                roleIds: SALES_DOMAIN,
            },
            {
                name: 'OrderSalesHome',
                path: '/orderSales',
                isNested: true,
                component: <OrderSalesHomePage />,
                title: 'Ventas',
                navbar: true,
                roleIds: SALES_DOMAIN,
            },
            {
                name: 'ReturnsHome',
                path: '/returns',
                isNested: true,
                component: <ReturnsHomePage />,
                title: 'Devoluciones',
                navbar: true,
                roleIds: SALES_DOMAIN,
            },
            {
                name: 'ClientsHome',
                path: '/clients',
                isNested: true,
                component: <ClientsHomePage />,
                title: 'Clientes',
                navbar: true,
                roleIds: SALES_DOMAIN,
            },
            {
                name: 'OrdersToFulfillPage',
                path: '/ordersToFulfill',
                isNested: false,
                component: <OrdersToFulfill />,
                title: 'Pedidos por surtir',
                navbar: true,
                // Reordering is a production-planning decision (backend gates it
                // to Produccion main), but the board sits under Ventas and both
                // domains view it — so all four roles may reach the page.
                roleIds: [...PRODUCTION_DOMAIN, ...SALES_DOMAIN],
            },
            {
                name: 'SalesSummary',
                path: '/salesSummary',
                component: <SalesSummary />,
                title: 'Resumen de ventas',
                navbar: true,
                roleIds: SALES_DOMAIN,
            },
        ],
    },
    {
        title: 'Recursos Humanos',
        navbar: true,
        roleIds: HR_DOMAIN,
        routes: [
            {
                name: 'EmployeesHomePage',
                path: '/employees',
                isNested: true,
                component: <EmployeesHomePage />,
                title: 'Empleados',
                navbar: true,
                roleIds: HR_DOMAIN,
            },
            {
                name: 'PayrollHome',
                path: '/payroll',
                isNested: true,
                component: <PayrollHomePage />,
                title: 'Períodos',
                navbar: true,
                roleIds: HR_DOMAIN,
            },
        ],
    },
    {
        // Day-to-day expense operations. Split out of Administración
        // (2026-07-19) so the section name matches its contents.
        title: 'Gastos',
        navbar: true,
        roleIds: EXPENSES_DOMAIN,
        routes: [
            {
                name: 'ExpensesHome',
                path: '/expense-resources',
                isNested: true,
                component: <ExpensesHomePage />,
                title: 'Gastos',
                navbar: true,
                roleIds: EXPENSES_DOMAIN,
            },
            {
                name: 'TransfersHome',
                path: '/transfers',
                isNested: true,
                component: <TransfersHomePage />,
                title: 'Transferencias',
                navbar: true,
                roleIds: EXPENSES_DOMAIN,
            },
            {
                name: 'ResourcesHome',
                path: '/resources',
                isNested: true,
                component: <ResourcesHomePage />,
                title: 'Recursos',
                navbar: true,
                roleIds: EXPENSES_DOMAIN,
            },
            {
                name: 'ThirdPartyBalances',
                path: '/third_party_balances',
                isNested: false,
                component: <ThirdPartyBalances />,
                title: 'Balance de proveedores',
                navbar: true,
                roleIds: EXPENSES_DOMAIN,
            },
            {
                name: 'ExpensesSummary',
                path: '/expensesSummary',
                isNested: false,
                component: <ExpensesSummary />,
                title: 'Resumen de gastos',
                navbar: true,
                roleIds: EXPENSES_DOMAIN,
            },
        ],
    },
    {
        // Admin work only. The four pages moved here from the old
        // Administración are GENERAL_VIEW now, so roles 8/9 (Gastos /
        // Asistente Gastos) no longer see them — intended (2026-07-19).
        title: 'Administración',
        navbar: true,
        roleIds: GENERAL_VIEW,
        routes: [
            {
                // Super-only inside the section: routeAuthFilter drops it for
                // everyone else, so non-super admins see Administración
                // without this entry.
                name: 'UsersHomePage',
                path: '/admin',
                isNested: true,
                component: <UsersHomePage />,
                title: 'Usuarios',
                navbar: true,
                roleIds: [RoleId.Super],
            },
            {
                name: 'AccountsHome',
                path: '/accounts',
                isNested: true,
                component: <AccountsHomePage />,
                title: 'Cuentas',
                navbar: true,
                roleIds: GENERAL_VIEW,
            },
            {
                name: 'SalesVsExpensesHistoric',
                path: '/admin-dashboard',
                isNested: false,
                component: <SalesVsExpensesHistoric />,
                title: 'Punto de equilibrio',
                navbar: true,
                roleIds: GENERAL_VIEW,
            },
            {
                name: 'AccountingHome',
                path: '/accounting',
                isNested: false,
                component: <Accounting />,
                title: 'Contabilidad',
                navbar: true,
                roleIds: GENERAL_VIEW,
            },
            {
                name: 'BalancesHome',
                path: '/balances',
                isNested: false,
                component: <BalancesPage />,
                title: 'Saldos y pagos',
                navbar: true,
                roleIds: GENERAL_VIEW,
            },
        ],
    },
    {
        name: 'ActivitiesPage',
        path: '/activities',
        component: <ActivitiesPage />,
        title: 'Actividades',
        navbar: false,
        roleIds: GENERAL_VIEW,
    },
];

export const pages: Route[] = routeItems.reduce((acc, curr) => {
    if (isRouteGroup(curr)) {
        const routes = curr.routes;
        return acc.concat(routes);
    }
    acc.push(curr);
    return acc;
}, [] as Route[]);
